import { Company, FirmUserPermission, UserPermission } from 'api';
import { SubQuestion } from 'components/Documents/components/request/RequestFolder.props';
import { RequestedFile } from 'pages/RequestDocuments/components/FolderIcon/types';
import { MeasurementDate } from '../measurementDate';

// Store Firm info
export type Firm = Omit<FirmUserPermission, 'user_permissions'> & {
  user_permissions?: UserPermission[];
};

export const FIRM_LIST_STATUSES = {
  FORBIDDEN: 'forbidden',
  IDLE: 'idle',
  READY: 'ready',
} as const;

export type FirmListStatus = (typeof FIRM_LIST_STATUSES)[keyof typeof FIRM_LIST_STATUSES];

// Tasks Progress by Firm

export interface TasksProgress extends Pick<Firm, 'id' | 'name'> {
  companies?: TasksProgressCompany[];
}

export interface TasksProgressCompany extends Pick<Company, 'id' | 'name'> {
  company_measurement_dates?: TasksProgressCompanyMeasurementDate[];
}

export interface TasksProgressCompanyMeasurementDate {
  id?: number;
  measurement_date?: TasksProgressMeasurementDate;
  tasks?: Task[];
}

export interface TasksProgressMeasurementDate extends Omit<MeasurementDate, 'cmd_id'> {
  created_at?: Date;
  deleted_at?: Date;
  is_deleted?: boolean;
  updated_at?: Date;
}

export interface Task {
  category?: number;
  created_at: Date;
  id?: number;
  progress?: Progress;
  summary?: string;
  requested_files?: RequestedFile[];
  paired_questionnaire?: Array<ProgressDetails>;
  task: TaskProgressDetails;
}

export interface Progress {
  completed?: number;
  total?: number;
}

// Task Progress Details

export interface TaskProgressDetails {
  assigned_email?: string;
  assigned_name?: string;
  category?: number;
  company_measurement_date?: number;
  description?: string | null;
  extension_of?: number | null;
  id?: number;
  is_done?: boolean;
   is_delegated?: boolean;
  delegation_depth?: number;
  last_interaction?: Date;
  progress?: ProgressDetails[];
  requesting_user?: number;
  responsible_users?: ResponsibleUser[];
  summary?: string;
  company_id?: number;
  measurement_date?: MeasurementDate;
  firm: Firm;
  company_name?: string;
}

export interface ProgressDetails {
  answers?: Answer[];
  children_questions?: ProgressDetails[];
  created_at?: Date;
  document_request?: number;
  files?: File[];
  id?: number;
  is_uploaded?: boolean;
  name?: string;
  options?: Option[];
  parent_question?: number | null;
  question_type?: QuestionType;
  questionnaire?: number;
  tags?: null;
  updated_at?: Date;
}

export interface File {
  file_path?: string;
  file_type?: string;
  filename?: string;
  guid?: string;
  id?: number;
  uploaded_by?: {
    email?: string;
    full_name?: string;
    id?: number;
  };
  uploaded_date?: Date;
}

export type QuestionType = 'dropdown' | 'text' | 'date' | 'multiple';

export interface Option {
  id: number;
  name: string;
  is_deleted: boolean;
  deleted_at: string;
}

export interface Answer {
  created_at?: Date;
  date_answer?: null;
  id?: number;
  question?: number;
  selected_choice?: number | null;
  text_answer?: string;
  updated_at?: Date;
}

export interface ResponsibleUser {
  email?: string;
  full_name?: string;
  id?: number;
}

export interface FirmCreateDocRequestPayload {
  firm: number;
  requested_documents: {
    firm: number;
    name: string;
    description: string;
  }[];
}

export interface Question {
  company: number;
  children_questions?: SubQuestion[];
  description?: string;
  name: string;
  new_options?: string[];
  options?: Option[];
  question_type: number;
}

export type FirmCreateQuestionRequestPayload = {
  firm: number;
  questions: Question[];
};
